import React from 'react';
import { View } from 'react-native';
import { Button, Card, Icon, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { formatMoney } from './offerCatalogue';

/** Privacy-minimized aggregates from `GET /api/referrals/partner/` (no customer PII). */
export interface PartnerSummaryAmounts {
  maturing?: number; payable?: number; in_payout?: number; paid?: number;
  under_review?: number; reconciled_cash_out_cents?: number;
  /**
   * Carried refund/dispute offsets already netted in the completed Transfers
   * behind `paid` (`paid` stays the earned amount; `paid - this` is what Stripe
   * moved). Absent on legacy responses.
   */
  offset_deducted_cents?: number;
  /**
   * Restitution credits (dispute won / refund reversed after its offset was
   * netted) added to those same Transfers: `paid - offset_deducted_cents + this`
   * is what Stripe moved. Absent on legacy responses.
   */
  credit_added_cents?: number;
}
export interface PartnerSummary {
  code?: string | null; status?: string; vat_regime?: string;
  /** Automatic code offered before the single initial choice; not shareable (F1). */
  suggested_code?: string | null;
  /** Server-composed canonical share link for `code` (`…/register/choice?ref=CODE`). */
  share_url?: string | null;
  can_choose_code?: boolean;
  current_terms?: { maturity_days?: number; currency?: string } | null;
  counts?: { referrals?: number; paying_referrals?: number };
  amounts_cents?: PartnerSummaryAmounts;
  /**
   * D-057 carried post-transfer recovery compensation still owed by the
   * partner: unreserved, unconsumed offsets deducted from future commissions,
   * never an automatic bank debit. Absent on legacy responses; only a positive
   * integer is a known, displayable value.
   */
  pending_offset_cents?: number;
}

interface Props {
  summary: PartnerSummary | null; loading: boolean; error: boolean; currency: string;
  regime?: string | null; onRetry: () => void;
}

const ALWAYS: Array<{ key: keyof PartnerSummaryAmounts; label: string; icon: string }> = [
  { key: 'maturing', label: 'stageMaturing', icon: 'timer-sand' },
  { key: 'payable', label: 'stagePayable', icon: 'check-circle-outline' },
  { key: 'in_payout', label: 'stageInPayout', icon: 'progress-clock' },
  { key: 'paid', label: 'stagePaid', icon: 'bank-transfer-out' },
];
const CONDITIONAL: Array<{ key: keyof PartnerSummaryAmounts; label: string; icon: string }> = [
  { key: 'under_review', label: 'stageUnderReview', icon: 'alert-circle-outline' },
  { key: 'reconciled_cash_out_cents', label: 'stageReconciled', icon: 'bank-transfer-out' },
];

/**
 * Truthful commission metrics. Amounts come only from the API; a Stripe Transfer
 * is never presented as bank receipt, and empty/unknown states stay honest.
 */
export function MetricsCard({ summary, loading, error, currency, regime, onRetry }: Props) {
  const { t, i18n } = useTranslation('partner');
  const theme = useTheme();
  const amounts = summary?.amounts_cents || {};
  const counts = summary?.counts || {};
  const currencyCode = summary?.current_terms?.currency || currency || 'eur';
  const maturityDays = summary?.current_terms?.maturity_days;
  // Only a positive known integer is a displayable carried compensation: legacy
  // (absent), zero, negative, non-integer or malformed values stay hidden rather
  // than being rendered as a fabricated or invalid money amount.
  const offsetCents = summary?.pending_offset_cents;
  const offsetKnown = typeof offsetCents === 'number' && Number.isSafeInteger(offsetCents) && offsetCents > 0;
  const offsetValue = offsetKnown ? (offsetCents as number) : 0;
  // Same rule for the offsets already deducted from completed Transfers.
  const deductedCents = amounts.offset_deducted_cents;
  const deductedKnown = typeof deductedCents === 'number' && Number.isSafeInteger(deductedCents) && deductedCents > 0;
  // And for the restitution credits added to completed Transfers.
  const creditedCents = amounts.credit_added_cents;
  const creditedKnown = typeof creditedCents === 'number' && Number.isSafeInteger(creditedCents) && creditedCents > 0;
  const empty = !loading && !error && !offsetKnown
    && Object.values(amounts).every(value => !value)
    && !counts.referrals && !counts.paying_referrals;

  const row = (icon: string, label: string, value: number, sign: '' | '−' | '+' = '') => <View key={label} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 }}>
      <Icon source={icon} size={20} color={theme.colors.primary} />
      <Text style={{ flexShrink: 1 }}>{t(label)}</Text>
    </View>
    {/* The amount never shrinks or wraps: a long label wraps instead (mobile). */}
    <Text variant="titleMedium" numberOfLines={1} style={{ flexShrink: 0 }}>{sign}{formatMoney(value, currencyCode, i18n.language)}</Text>
  </View>;

  return <Card testID="partner-metrics" style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12, paddingVertical: 12 }}>
    <Text variant="titleLarge">{t('metricsTitle')}</Text>
    {loading && <Text>{t('metricsLoading')}</Text>}
    {error && <View style={{ gap: 8 }}>
      <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{t('metricsError')}</Text>
      <Button mode="outlined" icon="refresh" contentStyle={{ minHeight: 44 }} onPress={onRetry}>{t('retry')}</Button>
    </View>}
    {!loading && !error && empty && <Text>{t('metricsEmpty')}</Text>}
    {!loading && !error && !empty && <>
      <View style={{ flexDirection: 'row', gap: 24, flexWrap: 'wrap' }}>
        <View><Text variant="labelMedium" style={{ color: theme.colors.onSurfaceVariant }}>{t('metricsReferrals')}</Text><Text variant="titleMedium">{counts.referrals ?? 0}</Text></View>
        <View><Text variant="labelMedium" style={{ color: theme.colors.onSurfaceVariant }}>{t('metricsPaying')}</Text><Text variant="titleMedium">{counts.paying_referrals ?? 0}</Text></View>
      </View>
      <View style={{ gap: 8 }}>
        {ALWAYS.map(stage => row(stage.icon, stage.label, amounts[stage.key] || 0))}
        {deductedKnown && row('cash-minus', 'stageOffsetDeducted', deductedCents as number, '−')}
        {creditedKnown && row('cash-plus', 'stageCreditAdded', creditedCents as number, '+')}
        {CONDITIONAL.filter(stage => (amounts[stage.key] || 0) > 0).map(stage => row(stage.icon, stage.label, amounts[stage.key] || 0))}
      </View>
      {offsetKnown && <View style={{ gap: 4 }}>
        {row('cash-minus', 'metricsPendingOffset', offsetValue)}
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('metricsPendingOffsetNote')}</Text>
      </View>}
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
        {typeof maturityDays === 'number' ? t('metricsMaturity', { days: maturityDays }) : t('metricsMaturityUnknown')}
      </Text>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
        {regime === 'normal' ? t('metricsVatNormal') : regime === 'exempt' ? t('metricsVatExempt') : t('metricsVatUnknown')}
      </Text>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('metricsNotBank')}</Text>
    </>}
  </Card.Content></Card>;
}
